import { ChangeEvent, useState } from 'react'

import { FileError, UploadResponse } from './MultiFileUpload.type'

const MAX_FILE_SIZE_MB = 25 // Tamanho máximo do arquivo em MB
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'application/pdf'] // Tipos de arquivos permitidos
const MAX_FILES = 10 // Número máximo de arquivos permitidos

const useMultiFileUpload = () => {
  const [files, setFiles] = useState<File[]>([])
  const [errors, setErrors] = useState<FileError[]>([])
  const [message, setMessage] = useState<string | null>(null)

  // Método para remover um arquivo
  const handleRemoveFile = (index: number): void => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
    setErrors([])
  }

  // Função que cria a lista de arquivos, quando o usuário seleciona um arquivo
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files) return

    const selectedFiles = Array.from(e.target.files)
    const validFiles: File[] = []
    const fileErrors: FileError[] = []

    // Validações globais
    const globalErrors = _validateTotalSizeAndFileCount(selectedFiles)
    if (globalErrors.length > 0) {
      setErrors(globalErrors)
      return
    }

    // Validações individuais de arquivo
    selectedFiles.forEach((file) => {
      const fileError = _validateFile(file)
      if (fileError) {
        fileErrors.push(fileError)
      } else {
        validFiles.push(file)
      }
    })

    setFiles((prev) => [...prev, ...validFiles])
    setErrors(fileErrors)
  }

  const _validateTotalSizeAndFileCount = (selectedFiles: File[]): FileError[] => {
    const errors: FileError[] = []

    if (_exceedsTotalSize(selectedFiles)) {
      errors.push({
        name: 'Limite de Tamanho',
        error: `O tamanho total dos arquivos não pode exceder ${MAX_FILE_SIZE_MB} MB.`
      })
    }

    if (_exceedsFileCount(selectedFiles)) {
      errors.push({
        name: 'Limite de Arquivos',
        error: `Você só pode enviar no máximo ${MAX_FILES} arquivos.`
      })
    }

    return errors
  }

  const _validateFile = (file: File): FileError | null => {
    if (!_isAllowedFileType(file)) {
      return {
        name: file.name,
        error: 'Tipo de arquivo não permitido.'
      }
    }

    if (_exceedsMaxFileSize(file)) {
      return {
        name: file.name,
        error: 'O arquivo excede o tamanho máximo de 25 MB.'
      }
    }

    if (_fileAlreadyExists(file)) {
      return {
        name: file.name,
        error: 'Arquivo já existe na lista.'
      }
    }

    return null
  }

  const _exceedsTotalSize = (selectedFiles: File[]): boolean => {
    const totalSize = _getTotalFilesSize(files) + _getTotalFilesSize(selectedFiles)
    const maxSizeInBytes = MAX_FILE_SIZE_MB * 1024 * 1024
    return totalSize > maxSizeInBytes
  }

  const _exceedsFileCount = (selectedFiles: File[]): boolean =>
    files.length + selectedFiles.length > MAX_FILES

  const _isAllowedFileType = (file: File): boolean => ALLOWED_FILE_TYPES.includes(file.type)

  const _exceedsMaxFileSize = (file: File): boolean => file.size > MAX_FILE_SIZE_MB * 1024 * 1024

  const _getTotalFilesSize = (filesList: File[]): number =>
    filesList.reduce((total, file) => total + file.size, 0)

  const _fileAlreadyExists = (file: File): boolean =>
    files.some((f) => f.name === file.name && f.size === file.size && f.type === file.type)

  const handleUpload = async (
    files: File[],
    name: string,
    leadId: string
  ): Promise<UploadResponse> => {
    if (files.length === 0) {
      setMessage('Por favor, selecione pelo menos um arquivo válido.')
      return Promise.reject(new Error('Nenhum arquivo válido foi selecionado.'))
    }

    try {
      return await _sendFilesToGoogleDrive(files, name, leadId)
    } catch (error) {
      console.warn(error)
      setMessage(`Erro ao enviar os arquivos. Erro: ${error}`)
      return Promise.reject(error)
    }
  }

  const _sendFilesToGoogleDrive = async (
    files: File[],
    name: string,
    leadId: string
  ): Promise<UploadResponse> => {
    if (!name || !leadId || files.length === 0) {
      throw new Error('Dados inválidos')
    }

    console.warn(
      `Início do upload dos arquivos. Nome: ${name}, Lead: ${leadId}, arquivos: ${files.length}`
    )

    try {
      const accessToken = await _getAccessToken()
      const folderId = await _createDriveFolder(name, accessToken)
      await _uploadFilesToDrive(folderId, files, accessToken)

      const googleDriveUrl = `https://drive.google.com/drive/folders/${folderId}`
      await _updateLeadWithDriveUrl(leadId, googleDriveUrl)

      return {
        folderLink: googleDriveUrl,
        leadId: leadId
      }
    } catch (error) {
      throw new Error('Erro ao enviar arquivos para o Google Drive')
    }
  }

  const _getAccessToken = async (): Promise<string> => {
    const response = await fetch('/api/google-drive-token', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })

    if (!response.ok) {
      throw new Error('Erro ao obter token de acesso')
    }

    const { accessToken } = await response.json()
    return accessToken
  }

  const _createDriveFolder = async (name: string, accessToken: string): Promise<string> => {
    const folderMetadata = {
      name: `${name} | ${new Date().toLocaleString('pt-BR').replace(/\//g, '-')}`,
      mimeType: 'application/vnd.google-apps.folder',
      parents: [process.env.NEXT_PUBLIC_DRIVE_FOLDER_ID]
    }
    console.warn('Criando pasta no Google Drive:', folderMetadata)
    console.warn('VARIAVEL', process.env.NEXT_PUBLIC_DRIVE_FOLDER_ID)
    const response = await fetch('https://www.googleapis.com/drive/v3/files', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(folderMetadata)
    })

    if (!response.ok) {
      throw new Error(`Erro ao criar pasta: ${response.statusText}`)
    }

    const folder = await response.json()
    return folder.id
  }

  const _uploadFilesToDrive = async (folderId: string, files: File[], accessToken: string) => {
    const uploadPromises = files.map(async (file) => {
      const fileMetadata = {
        name: file.name,
        parents: [folderId]
      }

      const form = new FormData()
      form.append(
        'metadata',
        new Blob([JSON.stringify(fileMetadata)], { type: 'application/json' })
      )
      form.append('file', file)

      const response = await fetch(
        'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`
          },
          body: form
        }
      )

      if (!response.ok) {
        throw new Error(`Erro ao fazer upload do arquivo ${file.name}: ${response.statusText}`)
      }

      return response.json()
    })

    await Promise.all(uploadPromises)
  }

  const _updateLeadWithDriveUrl = async (leadId: string, googleDriveUrl: string) => {
    await fetch('/api/salesforce/update-drive-url', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ leadId, googleDriveUrl })
    })
  }

  return {
    files,
    errors,
    message,
    setFiles,
    setErrors,
    setMessage,
    handleRemoveFile,
    handleUpload,
    handleFileChange,
    sendFilesToGoogleDrive: _sendFilesToGoogleDrive
  }
}

export default useMultiFileUpload
